// A membership billed by instalments, on its sheet (BL-009): "Bills $20.00 monthly, next on Dec 3,
// 2026.", where the latest bill stands with its invoice, and for the owner and the manager the next
// bill moved to another day (memberships.setBillDate) or billing paused with why and resumed
// (memberships.pauseBilling). While the company mirrors ServiceTitan the server refuses both, in words.

import { useState } from 'react';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText } from '../books/ui.tsx';
import { docHref } from '../shared/doc-link.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { BILL_WORDS, billDateProblem, billDays, type Billing } from './model.ts';

interface ChangeOut {
  billing: Billing;
  message: string;
}

/** The Billing value in the sheet's list: the summary, then the latest bill when it needs a look. */
export function BillingValue({ billing: b }: { billing: Billing }) {
  const latest = b.latest && b.latest.state !== 'paid' ? b.latest : null;
  return (
    <>
      {b.summary}
      {b.card && <span className="muted small block">Charged to {b.card.label}.</span>}
      {b.paused?.reason && <span className="muted small block">Paused: {b.paused.reason}</span>}
      {latest && (
        <span className="block member-bill">
          <span className={`chip say ${BILL_WORDS[latest.state][1]}`}>{BILL_WORDS[latest.state][0]}</span>{' '}
          <span className="muted small">the {dayText(latest.billOn)} bill</span>
          {latest.note && <span className="muted small block">{latest.note}</span>}
          {latest.invoiceId && (
            <a className="small block" href={docHref({ kind: 'invoice', id: latest.invoiceId })}>
              Open its invoice
            </a>
          )}
        </span>
      )}
    </>
  );
}

/** Change next bill date… and Pause billing… (or Resume billing), for the owner and the manager. */
export function BillingActions({
  billing: b,
  membershipId,
  name,
  today,
  onChanged,
}: {
  billing: Billing;
  membershipId: string;
  /** "Ana Ruiz’s Comfort Club membership". */
  name: string;
  today: string;
  /** After a change: what was said, and the billing as it is now. */
  onChanged: (said: { message: string }, billing: Billing) => void;
}) {
  const [open, setOpen] = useState<'date' | 'pause' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (r: ChangeOut) => {
    setOpen(null);
    onChanged({ message: r.message }, r.billing);
  };
  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      done(await client.call<ChangeOut>('memberships.pauseBilling', { membershipId, paused: false }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (open === 'date')
    return (
      <BillDateForm
        billing={b}
        membershipId={membershipId}
        name={name}
        today={today}
        onClose={() => setOpen(null)}
        onDone={done}
      />
    );
  if (open === 'pause')
    return <PauseForm membershipId={membershipId} name={name} onClose={() => setOpen(null)} onDone={done} />;
  return (
    <div className="row member-billing-actions">
      {!b.paused && b.nextBillOn && (
        <button
          type="button"
          className="btn soft"
          aria-label={`Change the next bill date of ${name}…`}
          onClick={() => setOpen('date')}
        >
          Change next bill date…
        </button>
      )}
      {b.paused ? (
        <button type="button" className="btn soft" disabled={busy} onClick={() => void resume()}>
          {busy ? 'Resuming…' : 'Resume billing'}
        </button>
      ) : (
        b.nextBillOn && (
          <button
            type="button"
            className="btn soft"
            aria-label={`Pause billing for ${name}…`}
            onClick={() => setOpen('pause')}
          >
            Pause billing…
          </button>
        )
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}

function BillDateForm({
  billing: b,
  membershipId,
  name,
  today,
  onClose,
  onDone,
}: {
  billing: Billing;
  membershipId: string;
  name: string;
  today: string;
  onClose: () => void;
  onDone: (r: ChangeOut) => void;
}) {
  const days = billDays(today, b.endsOn);
  const [on, setOn] = useState(b.nextBillOn ?? today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const problem = billDateProblem(on, days, dayText);
  const save = async () => {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await client.call<ChangeOut>('memberships.setBillDate', { membershipId, nextBillOn: on }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid member-status-form"
      aria-label={`Change the next bill date of ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <label>
        <span className="flabel">Next bill</span>
        <input
          className="field"
          type="date"
          min={days.min}
          max={days.max}
          value={on}
          onChange={(e) => setOn(e.target.value)}
        />
      </label>
      <p className="wide small">
        The bills after it follow on that day of the month (a shorter month: its last day). Nothing is billed
        for the days in between.
      </p>
      {tried && problem && (
        <p className="error small wide" role="alert">
          {problem}
        </p>
      )}
      <div className="row-actions wide">
        <button type="button" className="btn soft" onClick={onClose}>
          Back
        </button>
        <SaveButton busy={busy} busyLabel="Moving…" error={error} onClick={() => void save()}>
          Move the next bill
        </SaveButton>
      </div>
    </form>
  );
}

function PauseForm({
  membershipId,
  name,
  onClose,
  onDone,
}: {
  membershipId: string;
  name: string;
  onClose: () => void;
  onDone: (r: ChangeOut) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onDone(
        await client.call<ChangeOut>('memberships.pauseBilling', {
          membershipId,
          paused: true,
          reason: reason.trim(),
        }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid member-status-form"
      aria-label={`Pause billing for ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="wide small">
        Nothing is billed until it’s resumed. Resumed, it bills next on its own day: the bills while it was
        paused aren’t billed later. The membership itself stays as it is.
      </p>
      <label className="wide">
        <span className="flabel">Why</span>
        <input
          className="field"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Waiting on a new card, paid ahead by cheque…"
        />
      </label>
      {tried && !reason.trim() && (
        <p className="error small wide" role="alert">
          Say why billing is paused.
        </p>
      )}
      <div className="row-actions wide">
        <button type="button" className="btn soft" onClick={onClose}>
          Back
        </button>
        <SaveButton busy={busy} busyLabel="Pausing…" error={error} onClick={() => void save()}>
          Pause billing
        </SaveButton>
      </div>
    </form>
  );
}
